import React, { useMemo } from 'react';
import {
  Typography, Box, Button, TextField, Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  DEFAULT_PRODUCTION_RATES, RATE_UNIT_LABELS, DEFAULT_VISITS_PER_YEAR,
  MODIFIER_MULTIPLIERS, SERVICE_LABELS
} from './siteTakeoffConstants';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  rateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  rateLabel: {
    flex: 1,
    fontSize: '0.78rem',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rateInput: {
    width: 80,
    '& input': { fontSize: '0.78rem', padding: '6px 8px', textAlign: 'right' },
  },
  freqInput: {
    width: 50,
    '& input': { fontSize: '0.78rem', padding: '6px 6px', textAlign: 'right' },
  },
  unitLabel: {
    fontSize: '0.70rem',
    color: '#888',
    width: 42,
    textAlign: 'left',
  },
  freqLabel: {
    fontSize: '0.68rem',
    color: '#7b1fa2',
    width: 28,
    textAlign: 'left',
  },
  covInput: {
    width: 50,
    '& input': { fontSize: '0.78rem', padding: '6px 6px', textAlign: 'right' },
  },
  covLabel: {
    fontSize: '0.68rem',
    color: '#888',
    width: 14,
    textAlign: 'left',
  },
  modRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  modLabel: {
    flex: 1,
    fontSize: '0.78rem',
  },
  modInput: {
    width: 70,
    '& input': { fontSize: '0.78rem', padding: '6px 8px', textAlign: 'right' },
  },
}));

const MODIFIER_LABELS = {
  access_obstructed: 'Obstructed Access',
  slope_sloped: 'Sloped',
  litterExposure_high: 'High Litter',
  weedPressure_med: 'Med Weed Pressure',
  weedPressure_high: 'High Weed Pressure',
  limitedAccess: 'Limited Access',
  blowRestrictions: 'Blow Restrictions',
};

const ProductionRateEditor = ({ areas, rateOverrides, onChange }) => {
  const classes = useStyles();

  const usedServices = useMemo(() => {
    const used = new Set();
    (areas || []).forEach(area => {
      (area.servicesRequired || []).forEach(svc => used.add(svc));
    });
    return used;
  }, [areas]);

  const handleRateChange = (svc, value) => {
    const numVal = parseFloat(value);
    if (isNaN(numVal) || numVal < 0) return;
    onChange({
      ...rateOverrides,
      [svc]: { ...(rateOverrides[svc] || {}), rate: numVal },
    });
  };

  const handleFreqChange = (svc, value) => {
    const numVal = parseInt(value, 10);
    if (isNaN(numVal) || numVal < 1) return;
    onChange({
      ...rateOverrides,
      [svc]: { ...(rateOverrides[svc] || {}), visitsPerYear: numVal },
    });
  };

  const handleCoverageChange = (svc, value) => {
    const numVal = parseInt(value, 10);
    if (isNaN(numVal) || numVal < 1 || numVal > 100) return;
    onChange({
      ...rateOverrides,
      [svc]: { ...(rateOverrides[svc] || {}), coveragePerVisit: numVal / 100 },
    });
  };

  const handleVisitsPerYearChange = (value) => {
    const numVal = parseInt(value, 10);
    if (isNaN(numVal) || numVal < 1) return;
    onChange({ ...rateOverrides, _visitsPerYear: numVal });
  };

  const handleModifierChange = (key, value) => {
    const numVal = parseFloat(value);
    if (isNaN(numVal) || numVal < 1) return;
    onChange({
      ...rateOverrides,
      _modifiers: {
        ...(rateOverrides._modifiers || {}),
        [key]: numVal,
      },
    });
  };

  const handleReset = () => {
    onChange({});
  };

  const visibleServices = Object.entries(DEFAULT_PRODUCTION_RATES)
    .filter(([svc]) => usedServices.has(svc));

  if (visibleServices.length === 0) return null;

  const totalVisits = rateOverrides._visitsPerYear || DEFAULT_VISITS_PER_YEAR;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="subtitle2">Production Rates</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleReset}
          style={{ fontSize: '0.7rem', textTransform: 'none' }}
        >
          Reset to Defaults
        </Button>
      </div>

      {/* Total visits per year */}
      <div className={classes.rateRow} style={{ marginBottom: 8 }}>
        <span className={classes.rateLabel} style={{ fontWeight: 600 }}>Total Visits / Year</span>
        <TextField
          variant="outlined"
          size="small"
          type="number"
          className={classes.rateInput}
          value={totalVisits}
          onChange={(e) => handleVisitsPerYearChange(e.target.value)}
          inputProps={{ min: 1, max: 365 }}
        />
      </div>

      <Divider style={{ margin: '4px 0 8px' }} />

      {/* Column header */}
      <Box display="flex" alignItems="center" mb={0.5} style={{ gap: 6 }}>
        <span style={{ flex: 1, fontSize: '0.68rem', color: '#999', fontWeight: 600 }}>SERVICE</span>
        <span style={{ width: 80, fontSize: '0.68rem', color: '#999', fontWeight: 600, textAlign: 'right' }}>RATE</span>
        <span style={{ width: 42, fontSize: '0.68rem', color: '#999' }}></span>
        <span style={{ width: 50, fontSize: '0.68rem', color: '#999', fontWeight: 600, textAlign: 'right' }}>FREQ</span>
        <span style={{ width: 28 }}></span>
        <span style={{ width: 50, fontSize: '0.68rem', color: '#999', fontWeight: 600, textAlign: 'right' }}>COV</span>
        <span style={{ width: 14 }}></span>
      </Box>

      {visibleServices.map(([svc, config]) => {
        const currentRate = rateOverrides[svc]?.rate ?? config.rate;
        const currentFreq = rateOverrides[svc]?.visitsPerYear ?? config.visitsPerYear;
        const currentCov = rateOverrides[svc]?.coveragePerVisit ?? config.coveragePerVisit ?? 1.0;
        const isOverridden = rateOverrides[svc]?.rate !== undefined || rateOverrides[svc]?.visitsPerYear !== undefined || rateOverrides[svc]?.coveragePerVisit !== undefined;
        const isCovOverridden = rateOverrides[svc]?.coveragePerVisit !== undefined;
        return (
          <div key={svc} className={classes.rateRow}>
            <span className={classes.rateLabel} style={isOverridden ? { fontWeight: 600, color: '#1565c0' } : undefined}>
              {SERVICE_LABELS[svc] || config.label}
            </span>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              className={classes.rateInput}
              value={currentRate}
              onChange={(e) => handleRateChange(svc, e.target.value)}
              inputProps={{ min: 1, step: svc === 'tree_trimming' || svc === 'palm_trimming' ? 1 : 100 }}
            />
            <span className={classes.unitLabel}>
              {RATE_UNIT_LABELS[config.unit]}
            </span>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              className={classes.freqInput}
              value={currentFreq}
              onChange={(e) => handleFreqChange(svc, e.target.value)}
              inputProps={{ min: 1, max: 365 }}
            />
            <span className={classes.freqLabel}>x/yr</span>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              className={classes.covInput}
              value={Math.round(currentCov * 100)}
              onChange={(e) => handleCoverageChange(svc, e.target.value)}
              inputProps={{ min: 1, max: 100 }}
              style={isCovOverridden ? { backgroundColor: '#e3f2fd' } : undefined}
            />
            <span className={classes.covLabel}>%</span>
          </div>
        );
      })}

      <Divider style={{ margin: '8px 0' }} />

      <Typography variant="caption" style={{ fontWeight: 600 }}>
        Modifier Adjustments
      </Typography>

      {Object.entries(MODIFIER_MULTIPLIERS).map(([key, defaultVal]) => {
        const currentVal = rateOverrides._modifiers?.[key] ?? defaultVal;
        return (
          <div key={key} className={classes.modRow}>
            <span className={classes.modLabel}>{MODIFIER_LABELS[key] || key}</span>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              className={classes.modInput}
              value={currentVal}
              onChange={(e) => handleModifierChange(key, e.target.value)}
              inputProps={{ min: 1, max: 5, step: 0.05 }}
            />
            <span style={{ fontSize: '0.72rem', color: '#888' }}>x</span>
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(ProductionRateEditor);
